import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * Estado **nunca** é comunicado só por cor (PLANO_GERAL.md §4.4).
 *
 * O componente exige `children` (o rótulo textual) e aceita `icon`. Um badge sem texto é
 * impossível de construir por design — é a forma de garantir que a regra sobreviva às
 * próximas fases, quando aparecerem os estados de material, risco e impedimento.
 */
const badgeVariants = cva(
  'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium [&_svg]:size-3.5 [&_svg]:shrink-0',
  {
    variants: {
      tone: {
        neutral: 'border-(--color-state-neutral) text-(--color-state-neutral)',
        ok: 'border-(--color-state-ok) text-(--color-state-ok)',
        info: 'border-(--color-state-info) text-(--color-state-info)',
        warn: 'border-(--color-state-warn) text-(--color-state-warn)',
        risk: 'border-(--color-state-risk) text-(--color-state-risk)',
        danger: 'border-(--color-state-danger) text-(--color-state-danger)',
      },
      filled: {
        true: 'text-white',
        false: 'bg-transparent',
      },
    },
    compoundVariants: [
      { tone: 'neutral', filled: true, class: 'bg-(--color-state-neutral)' },
      { tone: 'ok', filled: true, class: 'bg-(--color-state-ok)' },
      { tone: 'info', filled: true, class: 'bg-(--color-state-info)' },
      { tone: 'warn', filled: true, class: 'bg-(--color-state-warn)' },
      { tone: 'risk', filled: true, class: 'bg-(--color-state-risk)' },
      { tone: 'danger', filled: true, class: 'bg-(--color-state-danger)' },
    ],
    defaultVariants: { tone: 'neutral', filled: false },
  },
);

export type BadgeProps = Omit<ComponentProps<'span'>, 'children'> &
  VariantProps<typeof badgeVariants> & {
    /** Rótulo textual — obrigatório. */
    children: ReactNode;
    icon?: ReactNode;
  };

export function Badge({ className, tone, filled, icon, children, ...props }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ tone, filled }), className)} {...props}>
      {icon}
      {children}
    </span>
  );
}
